import { useEffect, useRef } from 'react';
import HandPanel from './HandPanel';
import type { HandPanelProps } from './HandPanel';
import { HandCardState } from '@/types/game';

interface OpponentHandEntry {
  ownerId: string;
  cards: HandCardState[];
}

interface OpponentHandsProps {
  hands: OpponentHandEntry[];
  viewerId?: string | null;
  playerNames?: Record<string, string>;
  selectedCardId?: string | null;
  onSelectCard?: (cardId: string, ownerId: string) => void;
  onBoundsChange?: (rect: DOMRect | null) => void;
  selectionColor?: string | null;
}

const COLLAPSED_CARD_WIDTH = 48;

export default function OpponentHands({
  hands,
  viewerId,
  playerNames,
  selectedCardId,
  onSelectCard,
  onBoundsChange,
  selectionColor
}: OpponentHandsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!onBoundsChange) return;

    const updateBounds = () => {
      onBoundsChange(containerRef.current?.getBoundingClientRect() ?? null);
    };

    updateBounds();

    const observer = typeof ResizeObserver !== 'undefined' && containerRef.current
      ? new ResizeObserver(updateBounds)
      : null;
    observer?.observe(containerRef.current as Element);

    window.addEventListener('resize', updateBounds);
    window.addEventListener('scroll', updateBounds, true);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateBounds);
      window.removeEventListener('scroll', updateBounds, true);
      onBoundsChange(null);
    };
  }, [hands.length, onBoundsChange]);

  if (!hands.length) {
    return null;
  }

  return (
    <div ref={containerRef} className="pointer-events-none fixed left-0 right-0 top-3 z-[45] flex max-h-[calc(50vh-1rem)] flex-wrap justify-center gap-3 overflow-y-auto px-2 pb-2 sm:top-4 sm:gap-4 sm:px-4">
      {hands.map(hand => {
        const ownerName = playerNames?.[hand.ownerId] ?? 'Opponent';
        const handleSelectCard: HandPanelProps['onSelectCard'] = cardId => {
          onSelectCard?.(cardId, hand.ownerId);
        };

        return (
          <div
            key={hand.ownerId}
            className="pointer-events-none w-full max-w-[94vw] md:w-fit md:max-w-[40vw]"
          >
            <HandPanel
              cards={hand.cards}
              viewerId={viewerId}
              playerNames={playerNames}
              title={ownerName}
              emptyStateMessage="Empty hand"
              allowDrag={false}
              allowFlip={false}
              allowSelect
              cardWidthStrategy="adaptive"
              collapsedCardWidth={COLLAPSED_CARD_WIDTH}
              defaultCollapsed
              onSelectCard={handleSelectCard}
              selectedCardId={selectedCardId}
              selectionColor={selectionColor}
              cardFaceRotation={180}
              className="pointer-events-auto"
            />
          </div>
        );
      })}
    </div>
  );
}
